import { computed, reactive, ref, shallowRef, toRaw } from 'vue'
import type {
  CrudRequestStatus,
  CrudSort,
  PageQuery,
  PageResult
} from '@/types/crud'
import {
  isSupersededRequestError,
  useLatestRequest
} from '@/hooks/crud/useLatestRequest'

export interface UsePagedCrudOptions<TFilters extends object, TRecord> {
  createFilters: () => TFilters
  fetchPage: (
    query: PageQuery<TFilters>,
    signal: AbortSignal
  ) => Promise<PageResult<TRecord>>
  initialPageSize?: number
}

export function usePagedCrud<TFilters extends object, TRecord>(
  options: UsePagedCrudOptions<TFilters, TRecord>
) {
  const filters = reactive(options.createFilters()) as TFilters
  const pageNo = ref(1)
  const pageSize = ref(options.initialPageSize ?? 10)
  const sort = shallowRef<CrudSort>()
  const records = shallowRef<TRecord[]>([])
  const total = ref(0)
  const lastSuccessfulQuery = shallowRef<PageQuery<TFilters>>()
  const status = ref<CrudRequestStatus>('idle')
  const error = shallowRef<unknown | null>(null)
  const latestRequest = useLatestRequest()
  const loading = computed(() => status.value === 'loading')

  const createQuery = (): PageQuery<TFilters> => {
    const source = toRaw(filters) as Record<string, unknown>
    const cleanFilters = Object.entries(source).reduce<Record<string, unknown>>(
      (result, [key, value]) => {
        if (value !== '' && value !== undefined) result[key] = value
        return result
      },
      {}
    ) as Partial<TFilters>

    const query: PageQuery<TFilters> = {
      filters: cleanFilters,
      pageNo: pageNo.value,
      pageSize: pageSize.value
    }
    if (sort.value) query.sort = { ...sort.value }
    return query
  }

  const requestPage = (query: PageQuery<TFilters>) =>
    latestRequest.run((signal) => options.fetchPage(query, signal))

  const load = async (): Promise<boolean> => {
    status.value = 'loading'
    error.value = null

    try {
      let query = createQuery()
      let result = await requestPage(query)
      const maxPage = Math.max(1, Math.ceil(result.total / query.pageSize))

      if (query.pageNo > maxPage) {
        pageNo.value = maxPage
        if (result.total > 0) {
          query = createQuery()
          result = await requestPage(query)
        }
      }

      records.value = result.records
      total.value = result.total
      lastSuccessfulQuery.value = query
      status.value = result.records.length > 0 ? 'success' : 'empty'
      return true
    } catch (cause) {
      if (isSupersededRequestError(cause)) return false
      error.value = cause
      status.value = 'error'
      return false
    }
  }

  const search = () => {
    pageNo.value = 1
    return load()
  }

  const reset = () => {
    Object.assign(filters, options.createFilters())
    pageNo.value = 1
    return load()
  }

  const changePage = (value: number) => {
    pageNo.value = value
    return load()
  }

  const changePageSize = (value: number) => {
    pageSize.value = value
    pageNo.value = 1
    return load()
  }

  const changeSort = (value?: CrudSort) => {
    sort.value = value
    pageNo.value = 1
    return load()
  }

  const refreshAfterDelete = () => {
    if (records.value.length === 1 && pageNo.value > 1) {
      pageNo.value -= 1
    }
    return load()
  }

  return {
    filters,
    pageNo,
    pageSize,
    sort,
    records,
    total,
    lastSuccessfulQuery,
    status,
    error,
    loading,
    load,
    search,
    reset,
    changePage,
    changePageSize,
    changeSort,
    refresh: load,
    refreshAfterDelete
  }
}
